import _ from 'lodash'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { useCreateExpertiseDictaphoneMutation, useDeleteExpertiseDictaphoneMutation, useGetExpertiseQuery } from '@/core/services/expertise'

import useMediaDevices from '@/hooks/useMediaDevices'

import { useAppSelector } from '@/redux/rootReducer'
import { selectCustomDevices } from '@/redux/reducers/custom-device.reducer'

import Transcriptions from '../components/Transcriptions'
import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'

type TranscriptionProps = {
  expertiseId ?: string
}

export default function TranscriptionsContainer(props: TranscriptionProps) {
  const { expertiseId: paramExpertiseId } = useParams()
  const expertiseId = props.expertiseId || paramExpertiseId
  const { enqueueSnackbar } = useSnackbar()
  const { t } = useTranslation()

  const mediaDevices = useMediaDevices()

  const customDevices = useAppSelector(selectCustomDevices)

  const { data: expertise } = useGetExpertiseQuery(expertiseId ?? '', { skip: !expertiseId })
  const [createExpertiseDictaphone, { isLoading: isCreatingDictaphone }] = useCreateExpertiseDictaphoneMutation()
  const [deleteExpertiseDictaphone, { isLoading: isDeletingDictaphone }] = useDeleteExpertiseDictaphoneMutation()

  const [deletingDictaphone, setDeletingDictaphone] = useState<{ filename: string; key: string }>()
  const [openDeleteConfirmationDialog, setOpenDeleteConfirmationDialog] = useState(false)

  const devices = useMemo(() => {
    // merge custom devices with media devices
    const x = mediaDevices.map((device) => {
      return {
        id: device.deviceId,
        isCustom: false,
        isSelected: false,
        label: device.label,
        iconURL: '',
        type: device.kind,
      }
    })
    const y = _.map(customDevices, (customDevice) => {
      const device = _.find(mediaDevices, (mediaDevice) => mediaDevice.label === customDevice.device.name)
      return _.merge({}, customDevice, { available: !!device, device })
    }).map((customDevice) => {
      return {
        id: customDevice.device.deviceId,
        isCustom: true,
        isSelected: false,
        label: customDevice.device.name,
        iconURL: customDevice.iconURL,
        type: customDevice.device.type,
      }
    })
    return [...x, ...y]
  }, [mediaDevices, customDevices])

  const dictaphones = useMemo(
    () =>
      _.map(expertise?.dictaphones, (dictaphone) => ({
        filename: dictaphone.filename,
        key: dictaphone.key,
        src: dictaphone.url,
        text: dictaphone.text ?? '',
      })),
    [expertise]
  )

  return (
    <>
      <Transcriptions
        devices={devices}
        dictaphones={dictaphones}
        isUploading={isCreatingDictaphone}
        onDeleteDictaphone={(filename, key) => {
          setDeletingDictaphone({ filename, key })
          setOpenDeleteConfirmationDialog(true)
        }}
        onRecordingStopped={async (file) => {
          if (!expertiseId) return
          try {
            await createExpertiseDictaphone({ expertiseId, file }).unwrap()
            enqueueSnackbar(t('notification.expertise.dictaphone.success'), { variant: 'success' })
          } catch {
            enqueueSnackbar(t('notification.expertise.dictaphone.error'), { variant: 'error' })
          }
        }}
      />
      <DeleteConfirmationDialog
        isDeleting={isDeletingDictaphone}
        maxWidth={800}
        onClose={() => {
          setOpenDeleteConfirmationDialog(false)
        }}
        onDelete={async () => {
          if (!expertiseId || !deletingDictaphone) return
          await deleteExpertiseDictaphone({ expertiseId, filename: deletingDictaphone.filename, key: deletingDictaphone.key }).unwrap()
          setDeletingDictaphone(undefined)
          setOpenDeleteConfirmationDialog(false)
        }}
        open={openDeleteConfirmationDialog}
        title={t('dialog.deleteConfirmation.dictaphone.title')}
        message={t('dialog.deleteConfirmation.dictaphone.message')}
      />
    </>
  )
}
